import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'react-qr-code';
import { supabase } from '../supabase';
import html2pdf from 'html2pdf.js';
import './PrescriptionViewer.css';

function PrescriptionViewer({ prescription, mode = 'view', onClose }) {
    const paperRef = useRef(null);
    const [clinic, setClinic] = useState(null);
    const [doctor, setDoctor] = useState(null);
    const [patientExtras, setPatientExtras] = useState(null);

    useEffect(() => {
        async function loadData() {
            if (!prescription) return;

            // Fetch clinic to get all the new settings (logo, print_header_footer, theme, etc.)
            if (prescription.clinic_id) {
                const { data: clinicData } = await supabase
                    .from('clinics')
                    .select('*')
                    .eq('id', prescription.clinic_id)
                    .single();
                if (clinicData) setClinic(clinicData);
            }

            // Fetch doctor details if not fully present or for signature
            if (prescription.doctor_id) {
                const { data: doctorData } = await supabase
                    .from('users')
                    .select('phone, signature_url, qualification, specialization, registration_number, working_hours')
                    .eq('id', prescription.doctor_id)
                    .single();
                if (doctorData) setDoctor(doctorData);
            }

            // Fetch patient if missing extras (blood_group, conditions)
            if (!prescription.patients && prescription.patient_id) {
                const { data: patientData } = await supabase
                    .from('patients')
                    .select('blood_group, conditions')
                    .eq('id', prescription.patient_id)
                    .single();
                if (patientData) setPatientExtras(patientData);
            }
        }
        loadData();
    }, [prescription]);

    // Handle auto print / download
    useEffect(() => {
        if (clinic && doctor && (mode === 'print' || mode === 'download')) {
            const timer = setTimeout(() => {
                if (mode === 'print') {
                    window.print();
                } else if (mode === 'download' && paperRef.current) {
                    const opt = {
                        margin: 0, 
                        filename: `Prescription_${prescription.patient_name.replace(/\s+/g, '_')}_${prescription.id.split('-')[0]}.pdf`,
                        image: { type: 'jpeg', quality: 1.0 },
                        html2canvas: { scale: 3, useCORS: true, logging: false, letterRendering: true },
                        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
                    };
                    html2pdf().set(opt).from(paperRef.current).save().then(() => {
                        // Keep modal open or close? The user said "instantly download". 
                        // Keeping it open might be fine, or we close it so it feels like a stateless click.
                        onClose();
                    });
                }
            }, 800); // give time for fonts/images/QRs to paint
            return () => clearTimeout(timer);
        }
    }, [clinic, doctor, mode, prescription, onClose]);

    const [scale, setScale] = useState(1);
    const [isScaling, setIsScaling] = useState(false);

    // Auto-scaling logic to fit A4 on screen
    useEffect(() => {
        const updateScale = () => {
            if (!paperRef.current) return;

            const isMobile = window.innerWidth <= 768;
            
            if (isMobile) {
                // On mobile, scale to fit width exactly
                const targetWidth = window.innerWidth;
                const currentWidth = 800; // The fixed width from CSS
                const newScale = targetWidth / currentWidth;
                
                if (Math.abs(scale - newScale) > 0.01) {
                    setScale(newScale);
                    setIsScaling(true);
                }
            } else {
                // On desktop, scale to fit height if needed (e.g. A4 height is ~1122px)
                const targetHeight = window.innerHeight - 100;
                const currentHeight = paperRef.current.scrollHeight;

                if (currentHeight > targetHeight) {
                    const newScale = targetHeight / currentHeight;
                    if (Math.abs(scale - newScale) > 0.01) {
                        setScale(newScale);
                        setIsScaling(true);
                    }
                } else if (scale !== 1) {
                    setScale(1);
                    setIsScaling(false);
                }
            }
        };

        // Run once data is loaded and rendered
        if (clinic && doctor) {
            const timer = setTimeout(updateScale, 100);
            window.addEventListener('resize', updateScale);
            return () => {
                clearTimeout(timer);
                window.removeEventListener('resize', updateScale);
            };
        }
    }, [clinic, doctor, prescription, scale]);

    if (!prescription || !clinic) {
        return (
            <div className="prescription-viewer-overlay">
                <div className="prescription-loading">Loading prescription details...</div>
            </div>
        );
    }

    const pData = prescription.patients || patientExtras || {};

    // Settings defaults
    const printHeaderFooter = clinic.print_header_footer !== false;
    const themeColor = clinic.theme_color || '#ffffff';
    const showClinicPhone = clinic.show_clinic_phone !== false;
    const showDoctorPhone = clinic.show_doctor_phone === true;
    const showHours = clinic.show_working_hours !== false;
    const signatureType = clinic.signature_type || 'digital';

    const reportUrl = `${window.location.origin}/app/report/${prescription.id}`;

    return (
        <div className="prescription-viewer-overlay" onClick={onClose}>
            <button className="rx-close-btn no-print" onClick={onClose} title="Close">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>



            <div
                ref={paperRef}
                className={`prescription-paper ${!printHeaderFooter ? 'hide-header-footer-on-print' : ''} mode-${mode}`}
                id="prescription-pdf-content"
                style={{
                    '--theme-color': themeColor,
                    transform: (scale !== 1 && mode === 'view') ? `scale(${scale})` : 'none'
                }}
                onClick={e => e.stopPropagation()}
            >

                {/* HEADER */}
                <div className="rx-header">
                    <div className="rx-header-left">
                        {clinic.logo_url && <img src={clinic.logo_url} alt="Clinic Logo" className="rx-logo" />}
                        <div className="rx-clinic-info">
                            <h1 className="rx-clinic-name">{clinic.name}</h1>
                            <p className="rx-clinic-address">{clinic.address}</p>
                            <div className="rx-clinic-contact">
                                {showClinicPhone && clinic.phone && <span>📞 Clinic: {clinic.phone}</span>}
                                {showDoctorPhone && doctor?.phone && <span>📞 Dr: {doctor.phone}</span>}
                                {showHours && doctor?.working_hours && <span>🕒 {doctor.working_hours}</span>}
                            </div>
                        </div>
                    </div>

                    {doctor && (
                        <div className="rx-header-right">
                            <h2 className="rx-doctor-name">Dr. {prescription.doctor_name}</h2>
                            <p className="rx-doctor-qual">{doctor.qualification || prescription.doctor_qualification}</p>
                            <p className="rx-doctor-spec">{doctor.specialization || prescription.doctor_specialization}</p>
                            {doctor.registration_number && <p className="rx-doctor-reg">Reg No: {doctor.registration_number}</p>}
                        </div>
                    )}
                </div>

                <hr className="rx-divider rx-divider-header" />

                {/* PATIENT INFO */}
                <div className="rx-patient-info">
                    <div className="rx-patient-row">
                        <span><strong>Patient Name:</strong> {prescription.patient_name}</span>
                        <span><strong>Age/Gender:</strong> {prescription.patient_age} Y / {prescription.patient_gender}</span>
                        <span><strong>Date:</strong> {new Date(prescription.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="rx-patient-row">
                        <span><strong>Prescription ID:</strong> {prescription.id.split('-')[0].toUpperCase()}</span>
                        {(prescription.vitals?.bp || prescription.vitals?.blood_pressure) && <span><strong>BP:</strong> {prescription.vitals.bp || prescription.vitals.blood_pressure}</span>}
                        {prescription.vitals?.spo2 && <span><strong>SpO2:</strong> {prescription.vitals.spo2}%</span>}
                        {prescription.vitals?.pulse && <span><strong>Pulse:</strong> {prescription.vitals.pulse} bpm</span>}
                        {(prescription.vitals?.temperature) && <span><strong>Temp:</strong> {prescription.vitals.temperature} °F</span>}
                    </div>
                    {(pData.blood_group || pData.conditions) && (
                        <div className="rx-patient-row">
                            {pData.blood_group && <span><strong>Blood Group:</strong> {pData.blood_group}</span>}

                            {pData.conditions && <span><strong>Past Medical History:</strong> {pData.conditions}</span>}
                        </div>
                    )}
                </div>

                <hr className="rx-divider" />

                <div className="rx-body">
                    <div className="rx-sidebar">
                        <h1 className="rx-symbol">℞</h1>

                        <div className="rx-section">
                            <h3 className="rx-section-title">Symptoms</h3>
                            <p>{prescription.symptoms}</p>
                        </div>

                        {prescription.past_medical_history && (
                            <div className="rx-section">
                                <h3 className="rx-section-title">Past Medical History</h3>
                                <p>{prescription.past_medical_history}</p>
                            </div>
                        )}

                        {prescription.lmp && (prescription.patient_gender || '').toLowerCase() === 'female' && (
                            <div className="rx-section">
                                <h3 className="rx-section-title">LMP</h3>
                                <p>{new Date(prescription.lmp).toLocaleDateString()}</p>
                            </div>
                        )}

                        <div className="rx-section">
                            <h3 className="rx-section-title">Diagnosis</h3>
                            <p><strong>{prescription.diagnosis}</strong></p>
                        </div>



                        {prescription.advice && (
                            <div className="rx-section">
                                <h3 className="rx-section-title">Advice</h3>
                                <p>{prescription.advice}</p>
                            </div>
                        )}

                        {prescription.follow_up_date && (
                            <div className="rx-section">
                                <h3 className="rx-section-title">Follow Up</h3>
                                <p>{new Date(prescription.follow_up_date).toLocaleDateString()}</p>
                            </div>
                        )}
                    </div>

                    <div className="rx-main">
                        <h3 className="rx-section-title" style={{ marginTop: 0 }}>Medicines</h3>

                        {prescription.medicines && prescription.medicines.length > 0 ? (
                            <table className="rx-med-table">
                                <thead>
                                    <tr>
                                        <th>Medicine</th>
                                        <th>Dosage</th>
                                        <th>Duration</th>
                                        <th>Instructions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {prescription.medicines.map((med, i) => (
                                        <tr key={i}>
                                            <td>
                                                <strong>{med.name}</strong>
                                                {med.substitute_name && <div className="rx-substitute">Substitute: {med.substitute_name}</div>}
                                            </td>
                                            <td>{med.dosage} ({med.frequency})</td>
                                            <td>{med.duration}</td>
                                            <td>{med.instructions}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p style={{ color: 'var(--gray-500)' }}>No medicines prescribed.</p>
                        )}

                        {prescription.tests && prescription.tests.length > 0 && (
                            <div className="rx-section rx-compact-section">
                                <h4 className="rx-section-title">Lab Tests Prescribed</h4>
                                <ul className="rx-tests-list-compressed">
                                    {prescription.tests.map((test, i) => {
                                        const rawResults = prescription.lab_results || '';
                                        const hasResult = rawResults.includes(`"${test}"`);
                                        let resultDisplay = null;
                                        if (hasResult) {
                                            try {
                                                const parsed = typeof prescription.lab_results === 'string' ? JSON.parse(prescription.lab_results) : prescription.lab_results;
                                                if (parsed.structured && parsed.data && parsed.data[test]) {
                                                    resultDisplay = <span style={{ color: '#059669', fontSize: '0.8rem', marginLeft: '0.5rem', fontWeight: 500 }}>✓ Completed</span>;
                                                } else if (parsed[test]) {
                                                    resultDisplay = <span style={{ color: '#059669', fontSize: '0.85rem', marginLeft: '0.5rem' }}>— <strong>{parsed[test]}</strong></span>;
                                                } else {
                                                    resultDisplay = <span style={{ color: '#059669', fontSize: '0.8rem', marginLeft: '0.5rem', fontWeight: 500 }}>✓ Completed</span>;
                                                }
                                            } catch(e) {
                                                resultDisplay = <span style={{ color: '#059669', fontSize: '0.8rem', marginLeft: '0.5rem', fontWeight: 500 }}>✓ Completed</span>;
                                            }
                                        }
                                        
                                        return (
                                            <li key={i} style={{ marginBottom: '0.4rem', borderBottom: '1px dashed #e2e8f0', paddingBottom: '0.2rem' }}>
                                                <span>{test}</span>
                                                {resultDisplay || <span style={{ color: '#d97706', fontSize: '0.8rem', marginLeft: '0.5rem', fontWeight: 500 }}>⌛ Pending</span>}
                                            </li>
                                        );
                                    })}
                                </ul>

                                <div className="rx-lab-qr-compact">
                                    <div className="rx-qr-wrapper-small">
                                        <QRCode value={reportUrl} size={64} level="L" />
                                    </div>
                                    <div className="rx-qr-text-side">
                                        <p className="rx-qr-title">Live Report Tracking</p>
                                        <p className="rx-qr-desc">Scan this code to track your test status and view results once they are uploaded by the lab.</p>
                                        <a href={reportUrl} target="_blank" rel="noopener noreferrer" className="rx-report-link no-print">{reportUrl}</a>
                                        <span className="rx-report-link print-only">{reportUrl}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* FOOTER */}
                <hr className="rx-divider rx-divider-footer" />

                <div className="rx-footer">
                    <div className="rx-footer-disclaimer">
                        <p>This is a computer-generated prescription.</p>
                        <p>Generated on: {new Date().toLocaleString()}</p>
                        <p className="rx-disclaimer-bold">Follow up strictly if symptoms persist.</p>
                        <p className="rx-powered-by">Powered by <strong>Klinik</strong></p>
                    </div>
                    <div className="rx-footer-signature">
                        {(signatureType === 'digital' && doctor?.signature_url) ? (
                            <img src={doctor.signature_url} alt="Doctor's Signature" className="rx-signature-img" />
                        ) : signatureType === 'none' ? (
                            <div className="rx-no-signature" style={{ border: 'none', padding: '0.5rem' }}></div>
                        ) : (
                            <div className="rx-no-signature">Digitally Generated</div>
                        )}
                        <p className="rx-sig-name">Dr. {prescription.doctor_name}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PrescriptionViewer;
